"use client";

import { useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import { prepareChapters, type Chapter } from "@/lib/motion/page";
import { getLenis } from "@/lib/motion/smooth-scroll";

/**
 * Right-hand chapter rail for landing pages (the home page renders its own).
 * Built from the page's sections after mount; same markup/CSS as the home rail.
 * It keeps `id="home-dots"` so the footer can fade it out when it arrives.
 */
export function PageChapters() {
  const pathname = usePathname();
  const [chapters, setChapters] = useState<Array<Pick<Chapter, "label" | "accent">>>([]);
  const [active, setActive] = useState(0);
  const els = useRef<HTMLElement[]>([]);

  useEffect(() => {
    if (pathname === "/") {
      els.current = [];
      setChapters([]);
      return;
    }
    const list = prepareChapters();
    els.current = list.map((c) => c.el);
    setChapters(list.length >= 3 ? list.map(({ label, accent }) => ({ label, accent })) : []);
    setActive(0);

    let raf = 0;
    const update = () => {
      raf = 0;
      const line = window.innerHeight * 0.38;
      let current = 0;
      els.current.forEach((el, i) => {
        if (el.getBoundingClientRect().top <= line) current = i;
      });
      setActive(current);
    };
    const schedule = () => {
      if (!raf) raf = window.requestAnimationFrame(update);
    };
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    update();
    return () => {
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      if (raf) window.cancelAnimationFrame(raf);
    };
  }, [pathname]);

  if (!chapters.length) return null;

  const go = (i: number) => {
    const el = els.current[i];
    if (!el) return;
    const y = el.getBoundingClientRect().top + window.pageYOffset - (i === 0 ? 0 : 56);
    const lenis = getLenis();
    if (lenis) lenis.scrollTo(Math.max(0, y), { duration: 1.4 });
    else window.scrollTo({ top: Math.max(0, y), behavior: "smooth" });
  };

  const fill = chapters.length > 1 ? (active / (chapters.length - 1)) * 100 : 0;

  return (
    <nav className="home-dots" id="home-dots" aria-label="Sections" data-accent={chapters[active]?.accent ?? "signal"}>
      <span className="home-dots-line" aria-hidden="true">
        <i style={{ height: `${fill}%`, transition: "height .9s cubic-bezier(.16,1,.3,1)" }} />
      </span>
      {chapters.map((c, i) => (
        <button
          key={i}
          type="button"
          data-accent={c.accent}
          className={i === active ? "is-active" : undefined}
          aria-current={i === active ? "true" : "false"}
          onClick={() => go(i)}
        >
          <span>{c.label}</span>
        </button>
      ))}
    </nav>
  );
}
